/**
 * Schematic top-view icons for the sofa shapes.
 *
 * Placeholder visuals until real CGI tiles exist. Each shape is drawn as a
 * simple plan view (backrest, arms / chaises, seat cushions) and returned as
 * a data-URI SVG, so it drops straight into a tile background. The 160x120
 * viewBox matches the 4:3 tile so `background-size: cover` never crops.
 */

const BG = "#f5f3ed"; // tile background
const FRAME = "#cdc6b6"; // backrest, arms, chaise frame
const SEAT = "#e7e1d3"; // seat cushions
const LINE = "#b3ac9b"; // hairline outline

function rr(x: number, y: number, w: number, h: number, fill: string): string {
  return (
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="3" ` +
    `fill="${fill}" stroke="${LINE}" stroke-width="1.5"/>`
  );
}

function svg(body: string): string {
  const doc =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 120">` +
    `<rect width="160" height="120" fill="${BG}"/>${body}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(doc)}`;
}

/** Straight sofa, plan view — backrest along the top, arms at both ends. */
function straight(w: number): string {
  const y = 34;
  const h = 52;
  const arm = 12;
  const x = (160 - w) / 2;
  return svg(
    rr(x, y, w, 12, FRAME) +
      rr(x, y, arm, h, FRAME) +
      rr(x + w - arm, y, arm, h, FRAME) +
      rr(x + arm, y + 8, w - 2 * arm, h - 8, SEAT),
  );
}

/** L-shape — straight run plus a chaise. Defined chaise-right, mirrored for left. */
function lShape(chaise: "left" | "right"): string {
  // [x, y, w, h, fill], laid out for a chaise on the right.
  const right: [number, number, number, number, string][] = [
    [26, 28, 108, 11, FRAME], // backrest
    [26, 28, 12, 50, FRAME], // arm
    [94, 28, 40, 72, FRAME], // chaise frame
    [38, 35, 56, 43, SEAT], // straight seats
    [100, 35, 28, 60, SEAT], // chaise seat
  ];
  const rects =
    chaise === "right"
      ? right
      : right.map(([x, y, w, h, f]): [number, number, number, number, string] => [
          160 - x - w,
          y,
          w,
          h,
          f,
        ]);
  return svg(rects.map(([x, y, w, h, f]) => rr(x, y, w, h, f)).join(""));
}

/** U-shape — straight run with a chaise at each end. */
function uShape(): string {
  return svg(
    rr(24, 26, 112, 11, FRAME) +
      rr(24, 26, 38, 76, FRAME) +
      rr(98, 26, 38, 76, FRAME) +
      rr(30, 33, 26, 62, SEAT) +
      rr(62, 33, 36, 44, SEAT) +
      rr(104, 33, 26, 62, SEAT),
  );
}

const ICONS: Record<string, string> = {
  "f-2p": straight(76),
  "f-3p": straight(112),
  "f-4p": straight(140),
  "f-l-left": lShape("left"),
  "f-l-right": lShape("right"),
  "f-u": uShape(),
};

/** Data-URI SVG schematic for a forme. Falls back to a generic straight sofa. */
export function formeIcon(id: string): string {
  return ICONS[id] ?? straight(112);
}
